// pages/allergy/AllergyCheck.tsx
import React, { useEffect, useState, useRef } from "react";
import { Card, Input, Button, Flex } from "antd";
import { useNavigate } from "react-router-dom";
import {
  useFetchAllergyBySearchMutation,
  useDeleteAllergyMutation,
  useUploadAllergyChartMutation,
} from "./allergy.service";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux"; // ✅ ADD
import LocationSelector from "../../components/LocationSelector"; // ✅ ADD
import "./style.css";
import { toast } from "react-toastify";

export default function AllergyCheck() {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user); // ✅ ADD

  // ✅ Location state
  const [locationId, setLocationId] = useState(null);

  const [
    requestFetchAllergyBySearch,
    {
      data: allergyDataBySearch = {
        dishesWithAllergens: [],
        dishesWithoutAllergens: [],
      },
      isSuccess: isFetchBySearchSuccess,
    },
  ] = useFetchAllergyBySearchMutation();
  
  const [pdfName, setPdfName] = useState(null);
  const fileInputRef = useRef(null);

  const [deleteAllergy, { isLoading: isDeleteLoading }] =
    useDeleteAllergyMutation();

  const [
    uploadAllergyChart,
    { isSuccess: isUploadSuccess, isLoading: isUploadLoading },
  ] = useUploadAllergyChartMutation();

  const [allergens, setAllergens] = useState();

  // ✅ Set initial location from user
  useEffect(() => {
    const locId = user?.activeLocationId || user?.restaurantId;
    setLocationId(locId);
  }, [user]);

  // ✅ Handle location change
  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
  };

  const handleSearch = () => {
    if (!locationId) {
      toast.error("Please select a location first");
      return;
    }
    requestFetchAllergyBySearch({ 
      params: { allergens },
      locationId // ✅ Pass locationId
    });
  };

  const handleEditAllergy = (id) => {
    navigate(`/allergy/edit/new/${id}`);
  };

  const triggerPDFUpload = () => {
    fileInputRef.current?.click();
  };

  const handlePDFChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      try {
        setPdfName(file.name);
        uploadAllergyChart({ 
          pdfFile: file,
          locationId // ✅ Pass locationId
        }).unwrap();
      } catch (err) {
        console.error("Upload failed:", err);
        toast.error("Failed to upload PDF");
      }
    }
  };

  useEffect(() => {
    if (isUploadSuccess) {
      setPdfName(null);
      toast.success("PDF uploaded successfully");
    }
  }, [isUploadSuccess]);

  const { dishesWithAllergens, dishesWithoutAllergens } = allergyDataBySearch;

  return (
    <Card
      title={
        <Flex align="center" gap="middle" wrap>
          <span>{`${t("ALLERGY")} ${t("LIST")}`}</span>
          {/* ✅ Add LocationSelector */}
          <LocationSelector onLocationChange={handleLocationChange} />
        </Flex>
      }
      className="allergy-page"
      extra={
        <Flex justify="center">
          <Button 
            type="primary" 
            onClick={() => navigate("/allergy/create")}
            disabled={!locationId}
          >
            {t("CREATE")}
          </Button>
        </Flex>
      }
    >
      <div className="allergy-page-container">
        <div className="allergy-page-container-search">
          <div className="allergy-page-container-search-label">
            {t("CUSTOMER ALLERGY")}
          </div>
          <div className="allergy-page-container-search-input">
            <Input
              size="small"
              onChange={(e) => setAllergens(e.target.value)}
              disabled={!locationId}
              placeholder={locationId ? t("Search allergens...") : t("Please select a location first")}
            />
          </div>
          <div className="allergy-page-container-search-button">
            <Button 
              onClick={handleSearch} 
              size="small" 
              type="primary"
              disabled={!locationId}
            >
              {t("SEARCH")}
            </Button>
            <Button
              color="green"
              style={{ marginLeft: "10px" }}
              variant="solid"
              onClick={() => navigate("/allergy/edit/new")}
              disabled={!locationId}
            >
              {t("EDIT")}
            </Button>

            <Button
              color="grey"
              style={{ marginTop: "2px", marginLeft: "10px" }}
              variant="solid"
              onClick={triggerPDFUpload}
              loading={isUploadLoading}
              disabled={!locationId}
            >
              <svg
                viewBox="0 0 22 22"
                style={{
                  width: "12px",
                  height: "12px",
                  marginRight: "5px",
                  fill: "currentColor",
                }}
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM13 9V3.5L18.5 9H13zM12 18v-4H9l4-4 4 4h-3v4h-2z" />
              </svg>
              {pdfName ? `Uploaded: ${pdfName}` : "Upload PDF"}
              <input
                type="file"
                accept=".pdf"
                ref={fileInputRef}
                onChange={handlePDFChange}
                style={{ display: "none" }}
              />
            </Button>
          </div>
        </div>
        <div className="allergy-page-container-result">
          <span className="allergy-page-container-result-title">
            {t("RESULTS NOT INCLUDING ALLERGY INGREDIENT")}
          </span>
          {dishesWithoutAllergens.map((item, key) => (
            <div key={key} className="allergy-page-container-result-item">
              <p
                className="allergy-page-container-result-content"
                onClick={() => handleEditAllergy(item._id)}
                style={{ cursor: "pointer" }}
              >
                {item["name"][language] || item["name"]["default"]}
              </p>
            </div>
          ))}
          {dishesWithoutAllergens.length === 0 && (
            <p style={{ padding: "10px", color: "#999" }}>
              {locationId ? t("No dishes found") : t("Please select a location")}
            </p>
          )}
        </div>
        <div className="allergy-page-container-result">
          <span className="allergy-page-container-result-notitle">
            {t("RESULTS INCLUDING ALLERGY INGREDIENT")}
          </span>
          {dishesWithAllergens.map((item, key) => (
            <div key={key} className="allergy-page-container-result-item">
              <p
                className="allergy-page-container-result-content"
                onClick={() => handleEditAllergy(item._id)}
                style={{ cursor: "pointer" }}
              >
                {item["name"][language] || item["name"]["default"]}
              </p>
            </div>
          ))}
          {dishesWithAllergens.length === 0 && (
            <p style={{ padding: "10px", color: "#999" }}>
              {locationId ? t("No dishes found") : t("Please select a location")}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}